import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import cooking from "../assets/lottie-files/cooking.lottie";
import Modal from "react-bootstrap/Modal";
import { Row, Col, Table, Accordion } from "react-bootstrap";
import { sanityFetch } from "../sanityClient";

interface HandbookDocument {
  icon?: string;
  title: string;
  description?: string;
  url?: string;
  badge?: string;
  accentColor?: string;
}

interface VendorHandbook {
  introText?: string;
  documents?: HandbookDocument[];
}

interface ApplicationCategory {
  isOpen: boolean;
  statusMessage?: string;
  formUrl?: string;
}

interface VendorApplicationSettings {
  foodVendors: ApplicationCategory;
  artisansAndMakers: ApplicationCategory;
  farmersMessage?: string;
}

interface BoothSizes {
  sqft28?: number;
  sqft48?: number;
  sqft80?: number;
}

interface VendorFees {
  seasonYear?: string;
  indoor?: {
    farmers?: BoothSizes;
    vendors?: BoothSizes;
    preparedFood?: BoothSizes;
  };
  outdoor?: {
    farmerPrice?: number;
    vendorPrice?: number;
  };
  utilityFee?: number;
  storageRoomFee?: number;
  seniorDiscount?: number;
}

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const DEFAULT_FOOD_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSd2ZEoDAKLOF0Dwu1y5efNV3lXJ3rFvIqMNSgiNCR9N0l5wag/viewform";
const DEFAULT_ARTISAN_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSd2ZEoDAKLOF0Dwu1y5efNV3lXJ3rFvIqMNSgiNCR9N0l5wag/viewform";

const fmt = (val?: number) => (val != null ? `$${val.toFixed(2)}` : "N/A");

const LightCard = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid rgba(45,106,20,0.12)",
      borderRadius: 16,
      padding: "1.5rem",
      height: "100%",
      boxShadow: "0 2px 20px rgba(0,0,0,0.055)",
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2
      style={{
        fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
        fontWeight: 800,
        color: "#0d1b2a",
        margin: 0,
      }}
    >
      {children}
    </h2>
    <div
      style={{
        width: 56,
        height: 4,
        background: "linear-gradient(90deg, #6dc744, #4a8c25)",
        borderRadius: 2,
        marginTop: 8,
      }}
    />
  </div>
);

const FALLBACK_DOCS: HandbookDocument[] = [
  {
    icon: "📋",
    title: "Policy Manual",
    description: "The full SJFM Policy Manual. A living document — policies may change from time to time as needed.",
    badge: "Living Document",
    accentColor: "#2d6a14",
  },
  {
    icon: "⚖️",
    title: "SJFM By-Laws",
    description: "Governance and organizational structure of the St. John's Farmers' Market Cooperative.",
    accentColor: "#1e4a8a",
  },
  {
    icon: "📘",
    title: "Vendor Handbook",
    description: "Selection criteria, market policies, booth procedures, and operational details for vendors.",
    badge: "Required Reading",
    accentColor: "#c6252d",
  },
  {
    icon: "🍳",
    title: "Kitchen Handbook",
    description: "Guidelines for vendors and individuals who use the SJFM on-site licensed commercial kitchen.",
    accentColor: "#C19A3B",
  },
];

function VendorHandbookModal(props: { show: boolean; onHide: () => void; handbook: VendorHandbook | null }) {
  const { handbook } = props;
  const introText =
    handbook?.introText ??
    "The St. John's Farmers' Market Cooperative maintains several policy documents that outline the rules and procedures for markets and for all co-operative activities.";
  const docs = handbook?.documents?.length ? handbook.documents : FALLBACK_DOCS;

  return (
    <Modal {...props} size="lg" fullscreen="sm-down" aria-labelledby="vendor-handbook-modal" centered scrollable>
      <Modal.Header
        closeButton
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 100%)",
          borderBottom: "none",
          padding: "1.1rem clamp(1rem, 4vw, 2rem)",
        }}
      >
        <Modal.Title id="vendor-handbook-modal" style={{ color: "white", fontWeight: 800, fontSize: "clamp(1.05rem, 3vw, 1.3rem)" }}>
          <span style={{ marginRight: "0.5rem" }}>📘</span> Rules to Live By
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "#fffdf8", padding: "clamp(1rem, 4vw, 2rem)" }}>
        <p style={{ color: "#5a6a50", lineHeight: 1.75, marginBottom: "1.5rem", fontSize: "0.95rem" }}>
          {introText}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
          {docs.map((doc, i) => {
            const accent = doc.accentColor ?? "#2d6a14";
            const hasUrl = Boolean(doc.url);
            const cardInner = (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.9rem",
                  background: hasUrl ? "white" : "#f9f9f9",
                  border: `1px solid ${accent}22`,
                  borderLeft: `4px solid ${hasUrl ? accent : "#ccc"}`,
                  borderRadius: 12,
                  padding: "0.9rem 1rem",
                  transition: "box-shadow 0.2s, transform 0.2s",
                  cursor: hasUrl ? "pointer" : "default",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.045)",
                  opacity: hasUrl ? 1 : 0.7,
                }}
                onMouseEnter={(e) => {
                  if (!hasUrl) return;
                  const el = e.currentTarget as HTMLDivElement;
                  el.style.boxShadow = `0 6px 20px ${accent}28`;
                  el.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  if (!hasUrl) return;
                  const el = e.currentTarget as HTMLDivElement;
                  el.style.boxShadow = "0 2px 10px rgba(0,0,0,0.045)";
                  el.style.transform = "translateY(0)";
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 42,
                    height: 42,
                    minWidth: 42,
                    background: `${accent}15`,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.35rem",
                    marginTop: 2,
                  }}
                >
                  {doc.icon ?? "📄"}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.2rem" }}>
                    <span style={{ color: "#0d1b2a", fontWeight: 700, fontSize: "0.95rem" }}>{doc.title}</span>
                    {doc.badge && (
                      <span
                        style={{
                          background: `${accent}18`,
                          color: accent,
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          padding: "0.15em 0.55em",
                          borderRadius: 999,
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {doc.badge}
                      </span>
                    )}
                  </div>
                  {doc.description && (
                    <p style={{ color: "#7a8a70", margin: "0 0 0.5rem", fontSize: "0.83rem", lineHeight: 1.5 }}>
                      {doc.description}
                    </p>
                  )}
                  {/* CTA sits below description so it's always reachable on small screens */}
                  <span
                    style={{
                      display: "inline-block",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: hasUrl ? accent : "#bbb",
                    }}
                  >
                    {hasUrl ? "View document →" : "Coming soon"}
                  </span>
                </div>
              </div>
            );

            return hasUrl ? (
              <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                {cardInner}
              </a>
            ) : (
              <div key={i}>{cardInner}</div>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ background: "#f4fcf0", borderTop: "1px solid rgba(45,106,20,0.12)", padding: "0.85rem clamp(1rem, 4vw, 2rem)" }}>
        <button
          onClick={props.onHide}
          style={{
            background: "linear-gradient(135deg, #2d6a14, #4a8c25)",
            color: "white",
            border: "none",
            borderRadius: 999,
            padding: "0.65rem 2rem",
            fontWeight: 700,
            cursor: "pointer",
            fontSize: "0.95rem",
            width: "100%",
          }}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}

const BecomeVendor: React.FC = () => {
  const [modalShow, setModalShow] = useState(false);
  const [appSettings, setAppSettings] = useState<VendorApplicationSettings | null>(null);
  const [fees, setFees] = useState<VendorFees | null>(null);
  const [handbook, setHandbook] = useState<VendorHandbook | null>(null);

  useEffect(() => {
    sanityFetch<VendorApplicationSettings>(
      `*[_type == "vendorApplicationSettings"][0]{foodVendors, artisansAndMakers, farmersMessage}`
    ).then((data) => { if (data) setAppSettings(data); });
    sanityFetch<VendorFees>(
      `*[_type == "vendorFees"][0]{seasonYear, indoor, outdoor, utilityFee, storageRoomFee, seniorDiscount}`
    ).then((data) => { if (data) setFees(data); });
    sanityFetch<VendorHandbook>(
      `*[_type == "vendorHandbook"][0]{introText, "documents": documents[]{icon, title, description, badge, accentColor, "url": file.asset->url}}`
    ).then((data) => { if (data) setHandbook(data); });
  }, []);

  const seasonYear = fees?.seasonYear ?? "2025";
  const indoorFarmers = fees?.indoor?.farmers;
  const indoorVendors = fees?.indoor?.vendors;
  const indoorPrepared = fees?.indoor?.preparedFood;
  const outdoorFarmerPrice = fees?.outdoor?.farmerPrice ?? 35;
  const outdoorVendorPrice = fees?.outdoor?.vendorPrice ?? 50;
  const utilityFee = fees?.utilityFee ?? 10;
  const storageRoomFee = fees?.storageRoomFee ?? 3.75;
  const seniorDiscount = fees?.seniorDiscount ?? 5;

  return (
    <div>
      {/* ── HERO (dark, for contrast) ── */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3rem", position: "relative", zIndex: 1 }}>
          <motion.div style={{ flex: "1 1 480px", minWidth: 300 }} variants={stagger} initial="hidden" animate="visible">
            <motion.span
              variants={fadeUp}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}
            >
              <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
              Join Our Community
            </motion.span>

            <motion.h1
              variants={fadeUp}
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}
            >
              Become a{" "}
              <span style={{ background: "linear-gradient(90deg, #6dc744, #c6252d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Vendor
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "2rem", maxWidth: 520 }}
            >
              The heart of the St. John's Farmers' Market is our vendors — local growers, makers, and artisans offering everything from homemade baked goods to handcrafted accessories. Join our vibrant community and bring Newfoundland's best to our visitors.
            </motion.p>

            <motion.div variants={fadeUp} style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <a href="#apply" style={{ textDecoration: "none" }}>
                <button
                  style={{ background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", border: "none", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", cursor: "pointer", boxShadow: "0 4px 20px rgba(198,37,45,0.35)", transition: "transform 0.2s, box-shadow 0.2s" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 8px 28px rgba(198,37,45,0.5)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 20px rgba(198,37,45,0.35)"; }}
                >
                  Apply Now
                </button>
              </a>
              <button
                onClick={() => setModalShow(true)}
                style={{ background: "transparent", color: "#6dc744", border: "2px solid rgba(109,199,68,0.4)", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", cursor: "pointer", transition: "border-color 0.2s, background 0.2s" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#6dc744"; (e.currentTarget as HTMLButtonElement).style.background = "rgba(109,199,68,0.1)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(109,199,68,0.4)"; (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
              >
                📘 View Handbook
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            style={{ flex: "1 1 360px", minWidth: 280, display: "flex", justifyContent: "center" }}
          >
            <DotLottieReact src={cooking} loop autoplay style={{ width: "100%", maxWidth: 440 }} />
          </motion.div>
        </div>
      </section>

      {/* ── APPLICATION STATUS ── */}
      <section id="apply" style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}><SectionHeading>📅 Application Status</SectionHeading></motion.div>

            <Row xs={1} md={3} className="g-4">
              {/* Farmers */}
              <Col>
                <motion.div variants={fadeUp} style={{ height: "100%" }}>
                  <LightCard style={{ borderColor: "rgba(45,106,20,0.2)", borderTop: "4px solid #6dc744" }}>
                    <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🌾</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                      <h3 style={{ color: "#2d6a14", fontWeight: 700, fontSize: "1.1rem", margin: 0 }}>
                        Farmers & Primary Producers
                      </h3>
                      <span style={{ background: "#e8f8e0", color: "#2d6a14", fontWeight: 700, fontSize: "0.68rem", padding: "0.2em 0.7em", borderRadius: 999, letterSpacing: "0.06em" }}>
                        OPEN
                      </span>
                    </div>
                    <p style={{ color: "#4a5a40", lineHeight: 1.65, margin: 0 }}>
                      {appSettings?.farmersMessage ? (
                        appSettings.farmersMessage
                      ) : (
                        <>
                          Applications open <b>year-round</b>! Contact the Market Manager at{" "}
                          <a href="mailto:marketmanager@sjfm.ca" style={{ color: "#4a8c25", fontWeight: 700 }}>
                            marketmanager@sjfm.ca
                          </a>{" "}
                          to get started.
                        </>
                      )}
                    </p>
                  </LightCard>
                </motion.div>
              </Col>

              {/* Food Vendors */}
              <Col>
                {(() => {
                  const food = appSettings?.foodVendors;
                  const isOpen = food?.isOpen ?? true;
                  const formUrl = food?.formUrl ?? DEFAULT_FOOD_FORM_URL;
                  const message = food?.statusMessage ?? (isOpen ? "2026 applications are now open!" : "Applications are currently closed.");
                  return (
                    <motion.div variants={fadeUp} style={{ height: "100%" }}>
                      <LightCard style={{ borderTop: `4px solid ${isOpen ? "#c6252d" : "#ccc"}`, borderColor: isOpen ? "rgba(198,37,45,0.15)" : "rgba(0,0,0,0.08)", opacity: isOpen ? 1 : 0.75 }}>
                        <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🍞</div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                          <h3 style={{ color: isOpen ? "#c6252d" : "#888", fontWeight: 700, fontSize: "1.1rem", margin: 0 }}>Food Vendors</h3>
                          <span style={{ background: isOpen ? "#fdecea" : "#f0f0f0", color: isOpen ? "#c6252d" : "#999", fontWeight: 700, fontSize: "0.68rem", padding: "0.2em 0.7em", borderRadius: 999, letterSpacing: "0.06em" }}>
                            {isOpen ? "OPEN" : "CLOSED"}
                          </span>
                        </div>
                        <p style={{ color: "#4a5a40", lineHeight: 1.65, margin: 0, marginBottom: isOpen ? "1.25rem" : 0 }}>
                          {isOpen ? <><b>{message}</b> Apply today using our online form.</> : message}
                        </p>
                        {isOpen && (
                          <a href={formUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                            <button style={{ background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", border: "none", borderRadius: 999, padding: "0.6rem 1.4rem", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem" }}>
                              Apply Now →
                            </button>
                          </a>
                        )}
                      </LightCard>
                    </motion.div>
                  );
                })()}
              </Col>

              {/* Artisans */}
              <Col>
                {(() => {
                  const artisan = appSettings?.artisansAndMakers;
                  const isOpen = artisan?.isOpen ?? true;
                  const formUrl = artisan?.formUrl ?? DEFAULT_ARTISAN_FORM_URL;
                  const message = artisan?.statusMessage ?? (isOpen ? "2026 applications are now open!" : "Applications are currently closed.");
                  return (
                    <motion.div variants={fadeUp} style={{ height: "100%" }}>
                      <LightCard style={{ borderTop: `4px solid ${isOpen ? "#C19A3B" : "#ccc"}`, borderColor: isOpen ? "rgba(193,154,59,0.2)" : "rgba(0,0,0,0.08)", opacity: isOpen ? 1 : 0.75 }}>
                        <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🎨</div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                          <h3 style={{ color: isOpen ? "#C19A3B" : "#888", fontWeight: 700, fontSize: "1.1rem", margin: 0 }}>Artisans & Makers</h3>
                          <span style={{ background: isOpen ? "#fdf6e3" : "#f0f0f0", color: isOpen ? "#C19A3B" : "#999", fontWeight: 700, fontSize: "0.68rem", padding: "0.2em 0.7em", borderRadius: 999, letterSpacing: "0.06em" }}>
                            {isOpen ? "OPEN" : "CLOSED"}
                          </span>
                        </div>
                        <p style={{ color: "#4a5a40", lineHeight: 1.65, margin: 0, marginBottom: isOpen ? "1.25rem" : 0 }}>
                          {isOpen ? <><b>{message}</b> Apply today using our online form.</> : message}
                        </p>
                        {isOpen && (
                          <a href={formUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                            <button style={{ background: "linear-gradient(135deg, #C19A3B, #8a6d28)", color: "white", border: "none", borderRadius: 999, padding: "0.6rem 1.4rem", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem" }}>
                              Apply Now →
                            </button>
                          </a>
                        )}
                      </LightCard>
                    </motion.div>
                  );
                })()}
              </Col>
            </Row>
          </motion.div>
        </div>
      </section>

      {/* ── VENDOR HANDBOOK BANNER ── */}
      <section style={{ background: "#f2fbee", padding: "3rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div
              onClick={() => setModalShow(true)}
              style={{ background: "white", border: "1px solid rgba(45,106,20,0.18)", borderRadius: 20, padding: "2.5rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "2rem", flexWrap: "wrap", transition: "box-shadow 0.25s, border-color 0.25s", boxShadow: "0 4px 24px rgba(45,106,20,0.08)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = "0 8px 40px rgba(45,106,20,0.15)"; (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(45,106,20,0.35)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 24px rgba(45,106,20,0.08)"; (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(45,106,20,0.18)"; }}
            >
              <div style={{ fontSize: "4rem", lineHeight: 1, flexShrink: 0 }}>📘</div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <h2 style={{ color: "#0d1b2a", fontWeight: 800, fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)", margin: "0 0 0.4rem" }}>
                  Vendor Handbook
                </h2>
                <p style={{ color: "#5a6a50", margin: 0, fontSize: "1.05rem" }}>
                  Everything you need to know about becoming a vendor: selection criteria, market policies, and operational details.
                </p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setModalShow(true); }}
                style={{ background: "linear-gradient(135deg, #2d6a14, #4a8c25)", color: "white", border: "none", borderRadius: 999, padding: "0.8rem 1.8rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", fontSize: "0.95rem", boxShadow: "0 4px 16px rgba(45,106,20,0.25)", flexShrink: 0 }}
              >
                View Handbook →
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── VENDOR GUIDELINES ── */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}><SectionHeading>📋 Vendor Guidelines</SectionHeading></motion.div>

            <Row xs={1} md={2} className="g-4">
              {[
                { icon: "✅", color: "#4a8c25", bg: "#f2fbee", text: <>All products must be <b>handmade, grown, or produced</b> in Newfoundland and Labrador. If you don't <b>"Make it, Bake it, or Grow it,"</b> you can't sell it.</> },
                { icon: "⚠️", color: "#C19A3B", bg: "#fdf6e3", text: <>Due to high demand, we <b>cannot guarantee</b> a spot at every market. Confirm attendance <b>within 72 hours</b> of receiving your invitation. Late responses or cancellations may incur a fee.</> },
                { icon: "✅", color: "#4a8c25", bg: "#f2fbee", text: <>All vendors must follow policies in the{" "}<button onClick={() => setModalShow(true)} style={{ color: "#4a8c25", fontWeight: 700, textDecoration: "underline", cursor: "pointer", background: "none", border: "none", padding: 0, font: "inherit" }}>Vendor Handbook</button>{" "}with a focus on safety, food safety, and product suitability.</> },
                { icon: "⚠️", color: "#c6252d", bg: "#fdecea", text: <>The Market Manager and Board reserve the right to <b>deny participation</b> if Vendor Handbook policies are not followed.</> },
              ].map((item, i) => (
                <Col key={i}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ borderLeft: `4px solid ${item.color}`, background: item.bg, borderColor: `${item.color}25`, display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                      <span style={{ fontSize: "1.8rem", lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{item.icon}</span>
                      <p style={{ color: "#3a4a35", margin: 0, lineHeight: 1.65, fontSize: "1.02rem" }}>{item.text}</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </div>
      </section>

      {/* ── FOOD LICENSING STEPS ── */}
      <section style={{ background: "#f2fbee", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }}>
            <motion.div variants={fadeUp}><SectionHeading>🍎 Food Vendors & Licensing</SectionHeading></motion.div>

            <Row xs={1} sm={2} md={3} lg={4} className="g-4">
              {[
                { n: 1, icon: "📝", title: "Apply Online", desc: "Complete the Vendor Application form. Receive an automatic confirmation email." },
                { n: 2, icon: "📩", title: "Application Review", desc: "Market Manager reviews and contacts you within 2 weeks. If eligible, you'll receive required forms." },
                { n: 3, icon: "📄", title: "Conditional Approval", desc: "Receive a Conditional Approval Letter, needed for your food license application." },
                { n: 4, icon: "📧", title: "Submit to Inspector", desc: <> Email forms and approval letter to <a href="mailto:ehaa@gov.nl.ca" style={{ color: "#4a8c25" }}>ehaa@gov.nl.ca</a>.</> },
                { n: 5, icon: "🏛", title: "Service NL Review", desc: "Service NL processes your license. You'll be notified when approved." },
                { n: 6, icon: "🧑‍🍳", title: "Food Inspection", desc: "Market Manager schedules your on-site food inspection for your first market date." },
                { n: 7, icon: "🏅", title: "License Issuance", desc: <>Once you pass inspection, Service NL mails you the license. <b>Display it at your booth at all times.</b></> },
              ].map((step) => (
                <Col key={step.n}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ position: "relative", paddingTop: "2.25rem" }}>
                      <div style={{ position: "absolute", top: -14, left: 16, width: 30, height: 30, background: "linear-gradient(135deg, #6dc744, #4a8c25)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.82rem", color: "white", boxShadow: "0 2px 10px rgba(74,140,37,0.35)" }}>
                        {step.n}
                      </div>
                      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>{step.icon}</div>
                      <h4 style={{ color: "#0d1b2a", fontWeight: 700, fontSize: "0.98rem", marginBottom: "0.4rem" }}>{step.title}</h4>
                      <p style={{ color: "#5a6a50", margin: 0, fontSize: "0.88rem", lineHeight: 1.6 }}>{step.desc}</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
            </Row>

            <motion.div variants={fadeUp} style={{ marginTop: "1.5rem" }}>
              <LightCard style={{ borderLeft: "4px solid #C19A3B", borderColor: "rgba(193,154,59,0.2)", background: "#fdf6e3", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                <span style={{ fontSize: "2rem" }}>🍳</span>
                <p style={{ color: "#3a4a35", margin: 0 }}>
                  <b style={{ color: "#C19A3B" }}>Need a licensed kitchen?</b> Ask the Market Manager about renting our on-site licensed kitchen!
                </p>
              </LightCard>
            </motion.div>

            <motion.p variants={fadeUp} style={{ color: "#7a8a70", marginTop: "1rem", fontSize: "0.88rem" }}>
              <b style={{ color: "#3a4a35" }}>Note:</b> If you plan to introduce new food products that differ significantly from your original offerings, please consult with both the Market Manager and Service NL for approval.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── FOOD QUALITY STANDARDS ── */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}><SectionHeading>🧑‍🍳 Food Quality Standards</SectionHeading></motion.div>

            <Row xs={1} md={3} className="g-4">
              {[
                { icon: "🍲", color: "#4a8c25", bg: "#f2fbee", title: "On-Site Adjudication", desc: "All food vendors are reviewed by at least two staff members to ensure quality and receive constructive feedback." },
                { icon: "⏳", color: "#C19A3B", bg: "#fdf6e3", title: "Improvement Window", desc: "Vendors have one additional market date to address any concerns. Temporarily remove affected products until standards are met." },
                { icon: "📝", color: "#c6252d", bg: "#fdecea", title: "Seasonal Reviews", desc: "All food vendors are juried at least once per season; new vendors are reviewed twice in their first year." },
              ].map((item, i) => (
                <Col key={i}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ borderLeft: `4px solid ${item.color}`, background: item.bg, borderColor: `${item.color}22` }}>
                      <div style={{ fontSize: "2.2rem", marginBottom: "0.75rem" }}>{item.icon}</div>
                      <h4 style={{ color: "#0d1b2a", fontWeight: 700, marginBottom: "0.5rem" }}>{item.title}</h4>
                      <p style={{ color: "#5a6a50", margin: 0, lineHeight: 1.65 }}>{item.desc}</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
            </Row>

            <motion.div variants={fadeUp} style={{ marginTop: "2rem" }}>
              <blockquote style={{ background: "#f2fbee", border: "1px solid rgba(45,106,20,0.18)", borderLeft: "4px solid #6dc744", borderRadius: "0 12px 12px 0", padding: "1.5rem 2rem", margin: 0, fontStyle: "italic", color: "#4a5a40", fontSize: "1.05rem", lineHeight: 1.72 }}>
                "Our goal is to work with vendors to uphold the highest quality standards. Feedback is always constructive and intended to help you succeed at the Market."
                <br />
                <span style={{ fontWeight: 700, color: "#2d6a14", fontStyle: "normal" }}>– SJFM Market Team</span>
              </blockquote>
            </motion.div>

            <motion.p variants={fadeUp} style={{ color: "#7a8a70", marginTop: "1.5rem", fontSize: "0.92rem" }}>
              For full details on our quality standards, contact the Market Manager at{" "}
              <a href="mailto:marketmanager@sjfm.ca" style={{ color: "#4a8c25" }}>marketmanager@sjfm.ca</a>.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── CRAFTS & NON-FOOD ── */}
      <section style={{ background: "#f2fbee", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }}>
            <motion.div variants={fadeUp}><SectionHeading>🎨 Crafts, Specialty Items & Non-Food Products</SectionHeading></motion.div>

            <motion.p variants={fadeUp} style={{ color: "#4a5a40", marginBottom: "0.5rem", lineHeight: 1.7 }}>
              All vendors offering art, crafts, jewelry, clothing, and similar goods must be adjudicated before selling at the market.
            </motion.p>
            <motion.p variants={fadeUp} style={{ color: "#4a5a40", marginBottom: "2rem", lineHeight: 1.7 }}>
              If you wish to introduce a product significantly different from what you originally applied to sell, a new application is required.
            </motion.p>

            <motion.p variants={fadeUp} style={{ color: "#7a8a70", textTransform: "uppercase", fontSize: "0.78rem", letterSpacing: "0.1em", marginBottom: "1rem", fontWeight: 700 }}>
              How to Apply
            </motion.p>

            <Row xs={1} sm={2} md={4} className="g-4">
              {[
                { n: 1, icon: "📝", color: "#4a8c25", bg: "#f2fbee", title: "Apply", desc: "Submit the Vendor Application form. Receive a confirmation email." },
                { n: 2, icon: "🎨", color: "#C19A3B", bg: "#fdf6e3", title: "Artisan Form", desc: "Receive the Artisan Craft Application by email. Complete and return it." },
                { n: 3, icon: "👩‍⚖️", color: "#c6252d", bg: "#fdecea", title: "Adjudication", desc: "Attend your first market date. Two staff/board members adjudicate your products." },
                { n: 4, icon: "✅", color: "#4a8c25", bg: "#f2fbee", title: "Approved", desc: "Upon successful adjudication, you can book future market dates." },
              ].map((step) => (
                <Col key={step.n}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ textAlign: "center", background: step.bg, borderColor: `${step.color}22`, paddingTop: "2.5rem", position: "relative" }}>
                      <div style={{ position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", width: 28, height: 28, background: step.color, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.8rem", color: "white" }}>
                        {step.n}
                      </div>
                      <div style={{ fontSize: "2.2rem", marginBottom: "0.5rem" }}>{step.icon}</div>
                      <h4 style={{ color: "#0d1b2a", fontWeight: 700, fontSize: "1rem", marginBottom: "0.4rem" }}>{step.title}</h4>
                      <p style={{ color: "#5a6a50", margin: 0, fontSize: "0.88rem", lineHeight: 1.6 }}>{step.desc}</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
            </Row>

            <motion.p variants={fadeUp} style={{ color: "#7a8a70", marginTop: "1.5rem", fontSize: "0.88rem" }}>
              <b style={{ color: "#3a4a35" }}>Please note:</b> Periodic inspections may occur throughout the season. If your product quality declines, further adjudication may be required.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── FEES ── */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>

            {/* Indoor fees */}
            <motion.div variants={fadeUp}>
              <SectionHeading>💰 {seasonYear} Indoor Booth Fees</SectionHeading>
              <p style={{ color: "#5a6a50", marginBottom: "2rem", fontSize: "1.05rem", maxWidth: 680 }}>
                Booth fees are based on vendor category and booth size — all fees are per market date. Non-profit organizations can participate for <b style={{ color: "#4a8c25" }}>free!</b>
              </p>
            </motion.div>

            <Row xs={1} sm={2} md={4} className="g-4">
              {[
                { icon: "🌾", color: "#4a8c25", bg: "#f2fbee", title: "Farmers", rows: [["28 sq.ft (7×4)", fmt(indoorFarmers?.sqft28 ?? 40.04)], ["48 sq.ft (6×8)", fmt(indoorFarmers?.sqft48 ?? 68.64)], ["80 sq.ft", indoorFarmers?.sqft80 != null ? fmt(indoorFarmers.sqft80) : "N/A"]] },
                { icon: "🛍️", color: "#C19A3B", bg: "#fdf6e3", title: "Vendors", rows: [["28 sq.ft (7×4)", fmt(indoorVendors?.sqft28 ?? 47.74)], ["48 sq.ft (6×8)", fmt(indoorVendors?.sqft48 ?? 81.84)], ["80 sq.ft", indoorVendors?.sqft80 != null ? fmt(indoorVendors.sqft80) : "N/A"]] },
                { icon: "🍳", color: "#c6252d", bg: "#fdecea", title: "Prepared Food", rows: [["28 sq.ft (7×4)", indoorPrepared?.sqft28 != null ? fmt(indoorPrepared.sqft28) : "N/A"], ["48 sq.ft (6×8)", indoorPrepared?.sqft48 != null ? fmt(indoorPrepared.sqft48) : "N/A"], ["80 sq.ft", fmt(indoorPrepared?.sqft80 ?? 136.4)]] },
              ].map((card) => (
                <Col key={card.title}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ textAlign: "center", background: card.bg, borderTop: `4px solid ${card.color}`, borderColor: `${card.color}22` }}>
                      <div style={{ fontSize: "2.4rem" }}>{card.icon}</div>
                      <h4 style={{ color: card.color, fontWeight: 700, margin: "0.5rem 0 1rem" }}>{card.title}</h4>
                      {card.rows.map(([size, price]) => (
                        <div key={size} style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid rgba(0,0,0,0.06)", color: "#5a6a50", fontSize: "0.92rem" }}>
                          <span>{size}</span><b style={{ color: "#0d1b2a" }}>{price}</b>
                        </div>
                      ))}
                      <p style={{ color: "#9aaa90", fontSize: "0.78rem", marginTop: "0.75rem", marginBottom: 0 }}>One table included</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}

              {/* Non-profit */}
              <Col>
                <motion.div variants={fadeUp} style={{ height: "100%" }}>
                  <LightCard style={{ textAlign: "center", background: "#f2fbee", borderTop: "4px solid #6dc744", borderColor: "rgba(45,106,20,0.15)" }}>
                    <div style={{ fontSize: "2.4rem" }}>❤️</div>
                    <h4 style={{ color: "#4a8c25", fontWeight: 700, margin: "0.5rem 0 1rem" }}>
                      Non-Profit{" "}
                      <span style={{ background: "#d4f0c0", color: "#2d6a14", padding: "0.1em 0.55em", borderRadius: 999, fontSize: "0.68rem", verticalAlign: "middle" }}>FREE</span>
                    </h4>
                    {["28 sq.ft (7×4)", "48 sq.ft (6×8)", "80 sq.ft"].map((size) => (
                      <div key={size} style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid rgba(0,0,0,0.06)", color: "#5a6a50", fontSize: "0.92rem" }}>
                        <span>{size}</span><b style={{ color: "#4a8c25" }}>FREE</b>
                      </div>
                    ))}
                    <p style={{ color: "#9aaa90", fontSize: "0.78rem", marginTop: "0.75rem", marginBottom: 0 }}>One booth per Saturday</p>
                  </LightCard>
                </motion.div>
              </Col>
            </Row>

            <motion.p variants={fadeUp} style={{ color: "#9aaa90", fontSize: "0.82rem", marginTop: "1rem" }}>* HST will be added to all applicable rates</motion.p>

            <motion.div variants={fadeUp} style={{ marginTop: "1.5rem" }}>
              <Accordion>
                <Accordion.Item eventKey="0" style={{ border: "1px solid rgba(45,106,20,0.15)", borderRadius: 12, overflow: "hidden" }}>
                  <Accordion.Header>View Detailed Indoor Fees</Accordion.Header>
                  <Accordion.Body style={{ background: "#f9fdf7" }}>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr><th>Category</th><th>28 sq.ft (7×4)</th><th>48 sq.ft (6×8)</th><th>80 sq.ft</th><th>Notes</th></tr>
                      </thead>
                      <tbody>
                        <tr><td><b>Farmers</b></td><td>{fmt(indoorFarmers?.sqft28 ?? 40.04)}</td><td>{fmt(indoorFarmers?.sqft48 ?? 68.64)}</td><td>{indoorFarmers?.sqft80 != null ? fmt(indoorFarmers.sqft80) : "N/A"}</td><td>Primary producers only</td></tr>
                        <tr><td><b>Vendors</b></td><td>{fmt(indoorVendors?.sqft28 ?? 47.74)}</td><td>{fmt(indoorVendors?.sqft48 ?? 81.84)}</td><td>{indoorVendors?.sqft80 != null ? fmt(indoorVendors.sqft80) : "N/A"}</td><td>Artisans, crafts, makers, specialty food</td></tr>
                        <tr><td><b>Prepared Food</b></td><td>{indoorPrepared?.sqft28 != null ? fmt(indoorPrepared.sqft28) : "N/A"}</td><td>{indoorPrepared?.sqft48 != null ? fmt(indoorPrepared.sqft48) : "N/A"}</td><td>{fmt(indoorPrepared?.sqft80 ?? 136.4)}</td><td>Hot foods, food trucks, large setups</td></tr>
                        <tr><td><b>Non-Profit</b> <span style={{ background: "#c6252d", color: "#fff", fontWeight: 700, padding: "0.2em 0.6em", borderRadius: "1em", fontSize: "0.82rem" }}>FREE</span></td><td>$0</td><td>$0</td><td>$0</td><td>Registered non-profit organizations</td></tr>
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </motion.div>

            {/* Outdoor fees */}
            <motion.div variants={fadeUp} style={{ marginTop: "4rem" }}>
              <SectionHeading>🏞️ {seasonYear} Outdoor Booth Fees</SectionHeading>
              <p style={{ color: "#5a6a50", marginBottom: "2rem", fontSize: "1.05rem", maxWidth: 680 }}>
                Outdoor booth spaces are available during the {seasonYear} season. All fees are per market date and include one table.
              </p>
            </motion.div>

            <Row xs={1} md={3} className="g-4">
              {[
                { icon: "🌾", color: "#4a8c25", bg: "#f2fbee", title: "Farmer", price: `$${outdoorFarmerPrice}` },
                { icon: "🛍️", color: "#C19A3B", bg: "#fdf6e3", title: "Vendor", price: `$${outdoorVendorPrice}` },
              ].map((item) => (
                <Col key={item.title}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ textAlign: "center", background: item.bg, borderTop: `4px solid ${item.color}`, borderColor: `${item.color}22` }}>
                      <div style={{ fontSize: "2.4rem" }}>{item.icon}</div>
                      <h4 style={{ color: item.color, fontWeight: 700, margin: "0.5rem 0" }}>{item.title}</h4>
                      <div style={{ fontSize: "2rem", fontWeight: 800, color: "#0d1b2a", margin: "0.5rem 0 0.25rem" }}>
                        {item.price} <span style={{ fontSize: "0.9rem", color: "#9aaa90", fontWeight: 400 }}>+ HST</span>
                      </div>
                      <p style={{ color: "#9aaa90", fontSize: "0.78rem", margin: 0 }}>One table included</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
              <Col>
                <motion.div variants={fadeUp} style={{ height: "100%" }}>
                  <LightCard style={{ textAlign: "center", background: "#f2fbee", borderTop: "4px solid #6dc744", borderColor: "rgba(45,106,20,0.15)" }}>
                    <div style={{ fontSize: "2.4rem" }}>❤️</div>
                    <h4 style={{ color: "#4a8c25", fontWeight: 700, margin: "0.5rem 0" }}>Non-Profit</h4>
                    <div style={{ fontSize: "2rem", fontWeight: 800, color: "#4a8c25", margin: "0.5rem 0 0.25rem" }}>FREE</div>
                    <p style={{ color: "#9aaa90", fontSize: "0.78rem", margin: 0 }}>One table included</p>
                  </LightCard>
                </motion.div>
              </Col>
            </Row>

            <motion.p variants={fadeUp} style={{ color: "#9aaa90", fontSize: "0.82rem", marginTop: "1rem" }}>* HST will be added to all applicable rates</motion.p>

            <motion.div variants={fadeUp} style={{ marginTop: "1.5rem" }}>
              <Accordion>
                <Accordion.Item eventKey="1" style={{ border: "1px solid rgba(45,106,20,0.15)", borderRadius: 12, overflow: "hidden" }}>
                  <Accordion.Header>View Detailed Outdoor Fees</Accordion.Header>
                  <Accordion.Body style={{ background: "#f9fdf7" }}>
                    <Table striped bordered hover responsive>
                      <thead><tr><th>Category</th><th>Price</th><th>Notes</th></tr></thead>
                      <tbody>
                        <tr><td><b>Farmers</b></td><td>${outdoorFarmerPrice} + HST</td><td>One table included</td></tr>
                        <tr><td><b>Vendors</b></td><td>${outdoorVendorPrice} + HST</td><td>One table included</td></tr>
                        <tr><td><b>Non-Profit</b></td><td style={{ color: "#4a8c25", fontWeight: 700 }}>FREE</td><td>One table included</td></tr>
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </motion.div>

            {/* Other fees */}
            <motion.div variants={fadeUp} style={{ marginTop: "4rem" }}>
              <SectionHeading>📌 Other Vendor Fees & Discounts</SectionHeading>
            </motion.div>

            <Row xs={1} md={3} className="g-4">
              {[
                { icon: "⚡", color: "#4a8c25", bg: "#f2fbee", title: "Utility Fee", value: `$${utilityFee}`, sub: "per market date" },
                { icon: "📦", color: "#C19A3B", bg: "#fdf6e3", title: "Storage Room Fee", value: `$${storageRoomFee} + HST`, sub: "per sq.ft per month" },
                { icon: "🎉", color: "#c6252d", bg: "#fdecea", title: "Senior Discount (65+)", value: `$${seniorDiscount} off`, sub: "vendor fee, year round" },
              ].map((item, i) => (
                <Col key={i}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ textAlign: "center", background: item.bg, borderTop: `4px solid ${item.color}`, borderColor: `${item.color}22` }}>
                      <div style={{ fontSize: "2.4rem" }}>{item.icon}</div>
                      <h4 style={{ color: "#0d1b2a", fontWeight: 700, margin: "0.5rem 0" }}>{item.title}</h4>
                      <div style={{ fontSize: "1.6rem", fontWeight: 800, color: item.color }}>{item.value}</div>
                      <p style={{ color: "#9aaa90", fontSize: "0.82rem", margin: "0.25rem 0 0" }}>{item.sub}</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </div>
      </section>

      <VendorHandbookModal show={modalShow} onHide={() => setModalShow(false)} handbook={handbook} />
    </div>
  );
};

export default BecomeVendor;
